import useAction from '~/prix/react/hooks/action'
import styled from 'styled-components'
import theme from '~/design'
import { userRoleEnum } from '../../userObject'
import userListDefinition from '../userList.definition'
import List from '~/components/list'
import { useMedia } from 'react-use'
import { useNavigate } from 'react-router'
import RemoveIcon from '~/components/icons/ui/24px_bold-remove.svg'
import EditIcon from '~/components/icons/ui/24px_edit-72.svg'
import AddIcon from '~/components/icons/ui/24px_bold-add.svg'
import { format } from 'date-fns'
import { parseDateTime } from '~/prix/types/dateTime'
import useActionFunction from '~/prix/react/hooks/actionFunction'
import userDeleteDefinition from '../../delete/userDelete.definition'
import { toastError, toastSuccess } from '~/components/toasts'
import { useMemo, useState } from 'react'
import ViewIcon from '~/components/icons/ui/24px_eye-19.svg'
import useAPI from '~/prix/react/hooks/api'
import { canDeleteUser, canUpdateUserRole } from '../../userManagementPermissions'

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;

  @media (max-width: 870px) {
    padding: 30px 0;
  }

  .action-buttons {
    display: flex;
    align-items: center;
    gap: 5px;

    button {
      display: flex;
      align-items: center;
      justify-content: center;
      background-color: ${theme.colors.primary};
      border-radius: 5px;
      padding: 6px;
      border: none;

      cursor: pointer;
    }
  }
`

const TransparentButton = styled.button`
  display: flex;
  flex: 1;
  background-color: transparent;
  border: none;
  text-align: center;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 14px;
  cursor: pointer;

  &[disabled] {
    opacity: 0.6;
    background-color: transparent;
    border: none;
  }
`

interface UserListItemProps {
  id: string
  name: string
  email: string
  role: string
  rawRole: string
  createdAt: string
  updatedAt: string
}

const UserListScreen = () => {
  const [reloadKey, setReloadKey] = useState<number>(0)
  const userListData = useAction(userListDefinition, {}, [reloadKey], {
    autoLoad: true,
    staleWhileRevalidate: false,
  })
  const { callAction: deleteUserAction } = useActionFunction(userDeleteDefinition, {
    initialIsLoading: false,
  })

  const { result, isLoading } = userListData
  const navigate = useNavigate()
  const { context } = useAPI()
  const actorRole = context.user?.activeClient?.roles?.[0]
  const actorUserId = context.user?.id

  const firstBreak = useMedia('(max-width: 1230px)')
  const secondBreak = useMedia('(max-width: 1060px)')
  const thirdBreak = useMedia('(max-width: 540px)')

  const data = useMemo(() => {
    return userListData.result?.items?.map(item => ({
      id: item.id,
      name: item.name,
      email: item.email,
      role: userRoleEnum.find(role => role.value === item.role)?.label ?? item.role,
      rawRole: item.role,
      createdAt: format(parseDateTime(item.createdAt.toString()), 'dd/LL/yyyy'),
      updatedAt: format(parseDateTime(item.updatedAt.toString()), 'dd/LL/yyyy'),
    }))
  }, [result])

  const deleteUser = (id: string) => {
    const confirmed = window.confirm(
      'Este usuário perderá acesso a este cliente. A conta Firebase não será apagada.',
    )
    if (confirmed === true) {
      const { resultPromise } = deleteUserAction({ id })
      resultPromise
        .then(() => {
          toastSuccess('Acesso removido com sucesso')
          setReloadKey(prevKey => prevKey + 1)
        })
        .catch(error => {
          toastError(`Erro ao remover acesso. ${error.message}`)
        })
    }
  }

  return (
    <Wrapper>
      <div className='table'>
        <List
          title='Lista de usuários'
          items={data}
          isLoading={isLoading}
          itemType='usuários'
          extraButtons={
            <TransparentButton
              onClick={() => navigate('../users/create')}
              title='Cadastrar'
            >
              <AddIcon width={19} height={19} fill='#fff' />
            </TransparentButton>
          }
          fractions={
            firstBreak && !secondBreak
              ? {
                  title: 1.5 / 4,
                  targetPublic: 1 / 4,
                  startDate: 1 / 4,
                  action: 0.5 / 4,
                }
              : secondBreak && !thirdBreak
              ? {
                  title: 1.5 / 3,
                  startDate: 1 / 3,
                  action: 0.5 / 3,
                }
              : thirdBreak
              ? {
                  title: 1.25 / 3,
                  startDate: 1 / 3,
                  action: 0.75 / 3,
                }
              : {
                  title: 1.5 / 5,
                  targetType: 1 / 5,
                  targetPublic: 1 / 5,
                  startDate: 1 / 5,
                  action: 0.5 / 5,
                }
          }
          labels={
            firstBreak && !secondBreak
              ? {
                  name: 'Nome',
                  email: 'E-mail',
                  role: 'Cargo',
                  createdAt: 'Data de criação',
                  updatedAt: 'Data de atualização',
                  action: 'Ações',
                }
              : secondBreak
              ? {
                  name: 'Nome',
                  role: 'Cargo',
                  action: 'Ações',
                }
              : {
                  name: 'Nome',
                  role: 'Cargo',
                  createdAt: 'Data de criação',
                  updatedAt: 'Data de atualização',
                  action: 'Ações',
                }
          }
          actions={(item: UserListItemProps) => (
            <div className='action-buttons'>
              <button
                title='Detalhes'
                onClick={() => navigate(`../users/${item.id}`)}
              >
                <ViewIcon width={14} height={14} fill='#fff' />
              </button>
              {canUpdateUserRole(actorRole, item.rawRole, item.rawRole) ? (
                <button
                  title='Editar'
                  onClick={() => navigate(`../users/update/${item.id}`)}
                >
                  <EditIcon width={14} height={14} fill='#fff' />
                </button>
              ) : null}
              {actorUserId !== item.id && canDeleteUser(actorRole, item.rawRole) ? (
                <button title='Remover acesso' onClick={() => deleteUser(item.id)}>
                  <RemoveIcon width={14} height={14} fill='#fff' />
                </button>
              ) : null}
            </div>
          )}
        />
      </div>
    </Wrapper>
  )
}

export default UserListScreen
